<template>
  <FlexCol gap-3 class="h-full min-h-0">
    <Title :title="paramScreenName" :breadcrumb="true">
      <SelectMultiple v-if="allHeaders" v-model="selectHeaders" :items="allHeaders" item-value="field" item-text="title" class="w-[240px]" disableClear />

      <DropDown v-if="reports">
        <template #action>
          <Button light label="Relatórios" icon="fa-solid fa-file-invoice" />
        </template>
        <template #default>
          <FlexCol gap-3 class="w-[320px] mt-2">
            <ButtonExport v-for="(report, i) in reports" :label="report.label" :key="i" :api="report.api" :name="report.name" :disabled="!filters || filters.length < 1" v-model:filters="combinedFilters" />
          </FlexCol>
        </template>
      </DropDown>

      <slot name="title-left" v-bind="{ filters, items, exportToXlsx, route }"></slot>

      <TheFilter @filter="filterAll" @clear="clearStorage()" :filters="filterObject" v-model:filterValues="filters" />

      <Button primary label="Voltar" icon="fa-solid fa-chevron-left" @click="router.back()" v-if="backButton" />

      <slot name="title-export" v-bind="{ filters, items, exportToXlsx, route }">
        <ButtonExport v-can="`${scope}.export`" v-if="!disableExport" :api="route" :name="paramScreenName" v-model:filters="combinedFilters" />
      </slot>

      <Button v-if="!disableRegister" id="addResourceBtn" :label="paramButtonNew" primary icon="fa-solid fa-circle-plus" @click="navigate(paramRouteAdd)" v-can="`${scope}.create`" />

      <div v-else>
        <slot name="customNavigation"></slot>
      </div>

      <slot name="title-buttons" v-bind="{ filters, items, exportToXlsx, route }"></slot>
    </Title>

    <slot name="middle-card"></slot>
    <TheLoader v-if="loader" />

    <div class="grow min-h-0 relative">
      <Flex gap-3 items-stretch class="h-full min-h-0">
        <div class="grow min-h-0 overflow-hidden">
          <TableFull :headers="computedHeaders" :items="items" v-model="tableControl" @selectedItem="handleSelectItem" @doubleClick="handleDoubleClick" @headersChange="handleHeadersChange" @resetLayout="handleResetLayout" dense>
            <template v-slot:actions="{ item }">
              <div class="flex gap-2">
                <slot name="actions" v-bind="{ item }"></slot>
                <div class="w-4">
                  <TheToolTip label="Editar">
                    <TableIconButton v-if="!disableEdit" icon="fa-pen" @click="handleEdit(item)" v-can="`${scope}.edit`" />
                  </TheToolTip>
                </div>
                <div class="w-4">
                  <TheToolTip label="Excluir" v-if="!disableDelete">
                    <TableIconButton icon="fa-trash" v-if="props.canDelete(item)" @click.stop="removeConfirm(item, $event)" v-can="`${scope}.delete`" />
                  </TheToolTip>
                </div>
              </div>
            </template>
            <template v-for="head in computedHeaders.filter((head) => head.field !== 'actions')" #[`${head.field}`]="{ item }">
              <slot :name="`${head.field}`" v-bind="{ item, head }"></slot>
            </template>
          </TableFull>
        </div>
        <div class="shrink-0" :class="[bigCard ? 'w-[1200px]' : 'w-[640px]']" v-if="selected && selectable"></div>
      </Flex>
      <Card class="absolute top-0 right-0 bottom-2 overflow-y-scroll" :class="[bigCard ? 'w-[1200px]' : 'w-[640px]']" v-if="selected && selectable">
        <FlexCol gap-3>
          <Flex items-center justify-between class="pb-3 border-b">
            <slot name="card-title" v-bind="{ item: selected }">
              <span>{{ paramTitleCard }}</span>
            </slot>
            <Flex items-center justify-end gap-3>
              <IconButton icon="fa-solid fa-pen" @click="handleEdit(selected)" />
              <IconButton icon="fa-solid fa-x" @click="selected = null" />
            </Flex>
          </Flex>
          <slot name="card" v-bind="{ item: selected }"></slot>
        </FlexCol>
      </Card>
    </div>

    <Modal :title="paramTitleModalDelete" v-model="dialogDelete" size="w-3/12" disableClose>
      <template v-slot:default>
        {{ paramAskDelete }} <b>{{ choosed.name }}</b> ?
      </template>
      <template v-slot:footer>
        <Button id="serviceGroupRemoveBtn" primary label="Remover" @click="removeAcept()" />
        <Button danger label="Cancelar" @click="dialogDelete = false" />
      </template>
    </Modal>
  </FlexCol>
</template>

<!-- 
=============== Slots
Slots para Tabela : Nome do "Field" no header
Slot para Ação : actions

-->
<script setup>
/* global defineProps, defineEmits */
import { ref, computed, onMounted, watch, onBeforeMount } from 'vue'
import { get, remove } from '@/crud.js'
import { useExport } from '@/composables/useExport'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const routeInfo = useRoute()
const { exportToXlsx, exportLoader } = useExport()

const props = defineProps({
  modelValue: Object,
  selectable: Boolean,
  disableDelete: Boolean,
  disableEdit: { type: Boolean, default: false },
  editInNewTab: { type: Boolean, default: false },
  refresh: Boolean,
  backButton: Boolean,
  disableRegister: { type: Boolean, default: false },
  disableExport: { type: Boolean, default: false },
  protocolRedirect: { type: Boolean, default: false },
  canDelete: { type: Function, default: () => true },
  bigCard: Boolean,
})

const emits = defineEmits('update:modelValue', 'update:refresh', 'removedItem', 'loaded')

const scope = ref(props.modelValue.scope ?? null)

const paramScreenName = computed(() => props.modelValue.screenName ?? '')
const paramButtonNew = ref(props.modelValue.buttonNew ?? '')
const paramAskDelete = ref(props.modelValue.askDelete ?? '')
const paramTitleModalDelete = ref(props.modelValue.titleModalDelete ?? '')
const paramTitleCard = ref(props.modelValue.titleCard ?? '')

const route = ref(props.modelValue.api ?? '')
const paramRouteAdd = ref(props.modelValue.routeAdd ?? '')
const paramRouteEdit = ref(props.modelValue.routeEdit ?? '')

const reports = ref(props.modelValue.reports)

const filterObject = ref(props.modelValue.filters)
const mandatoryFilters = ref(props.modelValue.mandatoryFilters ?? [])
const cloneValue = (value) => JSON.parse(JSON.stringify(value))
const hasFilterValue = (value) => {
  if (Array.isArray(value)) return value.length > 0
  return value !== undefined && value !== null && value !== ''
}

const ensureMandatoryFilters = (baseFilters = []) => {
  const currentFilters = Array.isArray(baseFilters) ? cloneValue(baseFilters) : []
  const requiredFilters = Array.isArray(mandatoryFilters.value) ? mandatoryFilters.value : []

  requiredFilters.forEach((requiredFilter) => {
    const index = currentFilters.findIndex((filter) => filter?.column === requiredFilter?.column)

    if (index === -1 || !hasFilterValue(currentFilters[index]?.value)) {
      if (index >= 0) {
        currentFilters.splice(index, 1)
      }
      currentFilters.push(cloneValue(requiredFilter))
    }
  })

  return currentFilters
}

const filters = ref(ensureMandatoryFilters(props.modelValue.filter ?? []))

const filterPerene = ref(props.modelValue.filterPerene)

const headers = ref(props.modelValue.headers ?? [])
const allHeaders = ref(props.modelValue.allHeaders)
const selectHeaders = ref([...(headers.value ?? []).map((el) => el.field)])
const headersLocal = ref([])

const buildActionHeader = () => ({
  title: 'Ações',
  field: 'actions',
  width: 160,
  itemClass: 'min-w-[160px] text-center ',
  headerClass: 'justify-center',
  disableOrder: true,
  movable: false,
  resizable: false,
})

headersLocal.value = [...(props.modelValue.headers ?? []), buildActionHeader()]

const resolveTableLimit = (value) => {
  const fallbackLimit = Number(props.modelValue.limit ?? 50)
  const maxLimit = Number(props.modelValue.maxLimit ?? 0)

  let numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    numeric = Number.isFinite(fallbackLimit) && fallbackLimit > 0 ? fallbackLimit : 50
  }

  if (Number.isFinite(maxLimit) && maxLimit > 0) {
    numeric = Math.min(numeric, maxLimit)
  }

  return numeric
}

const tableControl = ref({
  page: 1,
  total: 0,
  limit: resolveTableLimit(props.modelValue.limit),
  pages: 0,
  order: props.modelValue.order || [],
})

const choosed = ref(null)
const selected = ref(null)
const items = ref([])
const loader = ref(false)
const dialogDelete = ref(false)

const isLoading = ref(false)
const initializing = ref(true)

const computedHeaders = computed(() => {
  if (!allHeaders.value) {
    return headersLocal.value
  }
  const data = []
  const fixed = headers.value.filter((el) => el.fixed)
  const selectableHeaders = allHeaders.value.filter((el) => selectHeaders.value.includes(el.field))
  data.push(...fixed)
  data.push(...selectableHeaders)
  data.push(buildActionHeader())
  return data
})

const combinedFilters = computed(() => {
  const combined = [...(filters.value || [])]
  if (filterPerene.value && filterPerene.value.length > 0) {
    combined.push(...filterPerene.value)
  }
  return combined
})

const STORAGE_KEY = 'manage-shell-control'

const cloneHeaderList = (list = []) =>
  (Array.isArray(list) ? list : []).map((header) => ({
    ...header,
  }))

const extractHeaderState = () => {
  const visibleWidths = [...headers.value, ...(allHeaders.value || [])].reduce((acc, header) => {
    if (!header?.field) return acc
    if (header.width === null || header.width === undefined || header.width === '') return acc
    acc[header.field] = header.width
    return acc
  }, {})

  return {
    headersOrder: headers.value.map((header) => header.field),
    allHeadersOrder: Array.isArray(allHeaders.value) ? allHeaders.value.map((header) => header.field) : null,
    widths: visibleWidths,
  }
}

const applyHeaderLayout = (list = [], order = [], widths = {}) => {
  const currentList = cloneHeaderList(list)
  const headerMap = new Map(currentList.map((header) => [header.field, header]))
  const ordered = []

  order.forEach((field) => {
    const found = headerMap.get(field)
    if (!found) return
    ordered.push(found)
    headerMap.delete(field)
  })

  const remaining = currentList.filter((header) => headerMap.has(header.field))
  const result = [...ordered, ...remaining]

  return result.map((header) => ({
    ...header,
    width: Object.prototype.hasOwnProperty.call(widths, header.field) ? widths[header.field] : header.width,
  }))
}

const resetHeaderState = () => {
  headers.value = cloneHeaderList(props.modelValue.headers ?? [])
  allHeaders.value = Array.isArray(props.modelValue.allHeaders) ? cloneHeaderList(props.modelValue.allHeaders) : props.modelValue.allHeaders
  headersLocal.value = [...headers.value, buildActionHeader()]
  selectHeaders.value = [...(headers.value ?? []).map((el) => el.field)]
}

const clearStorage = () => {
  const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  allStates[routeInfo.name] = null
  localStorage.setItem(STORAGE_KEY, JSON.stringify(allStates))
  filters.value = ensureMandatoryFilters([])
  tableControl.value.page = 1
  tableControl.value.limit = resolveTableLimit(props.modelValue.limit)
  tableControl.value.order = Array.isArray(props.modelValue.order) ? [...props.modelValue.order] : []
  resetHeaderState()
}

const saveState = () => {
  if (!routeInfo.name) return

  try {
    const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    allStates[routeInfo.name] = {
      filters: filters.value,
      tableControl: {
        page: tableControl.value.page,
        limit: tableControl.value.limit,
        order: tableControl.value.order,
      },
      selectHeaders: selectHeaders.value,
      headerState: extractHeaderState(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allStates))
  } catch (error) {
    console.error('Erro ao salvar estado no localStorage:', error)
  }
}

const uniqueByKey = (array, key) => {
  const seen = new Set()
  return array.filter((item) => {
    if (seen.has(item[key])) return false
    seen.add(item[key])
    return true
  })
}

const loadState = () => {
  if (!routeInfo.name) return false

  try {
    const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    const state = allStates[routeInfo.name]

    if (!state) return false
    if (state.filters) {
      if (!filters.value || filters.value.length === 0) {
        filters.value = ensureMandatoryFilters(state.filters)
      } else {
        filters.value.push(...state.filters)
        filters.value = uniqueByKey(filters.value, 'column')
        filters.value = ensureMandatoryFilters(filters.value)
      }
    }

    if (state.tableControl) {
      if (state.tableControl.page) tableControl.value.page = state.tableControl.page
      if (state.tableControl.limit) tableControl.value.limit = resolveTableLimit(state.tableControl.limit)
      if (state.tableControl.order) tableControl.value.order = state.tableControl.order
    }
    if (state.headerState) {
      const widths = state.headerState.widths ?? {}
      headers.value = applyHeaderLayout(props.modelValue.headers ?? [], state.headerState.headersOrder ?? [], widths)
      if (Array.isArray(props.modelValue.allHeaders)) {
        allHeaders.value = applyHeaderLayout(props.modelValue.allHeaders, state.headerState.allHeadersOrder ?? [], widths)
      }
      headersLocal.value = [...headers.value, buildActionHeader()]
    }
    if (state.selectHeaders && allHeaders.value) {
      selectHeaders.value = state.selectHeaders
    }

    return true
  } catch (error) {
    console.error(error)
    return false
  }
}

const navigate = (routeName, paramObj) => {
  let route = {
    name: routeName,
    params: { ...(routeInfo.params || {}) },
  }
  if (paramObj) {
    route.params = { ...route.params, ...paramObj }
  }

  router.push(route)
}

const loadItems = async () => {
  if (isLoading.value) return
  isLoading.value = true

  try {
    loader.value = true
    tableControl.value.limit = resolveTableLimit(tableControl.value.limit)

    const query = {
      params: {
        page: tableControl.value.page,
        limit: tableControl.value.limit,
        order: tableControl.value.order,
        filters: [],
      },
    }

    if (filters.value?.length) query.params.filters.push(...filters.value)
    if (filterPerene.value?.length) query.params.filters.push(...filterPerene.value)

    const raw = await get(route.value, query)

    items.value = raw.data
    tableControl.value.pages = Math.ceil(raw.total / tableControl.value.limit)
    tableControl.value.total = raw.total
    emits('loaded', {
      items: items.value,
      total: tableControl.value.total,
      filters: JSON.parse(JSON.stringify(combinedFilters.value ?? [])),
    })
  } catch (error) {
    console.error(error.message)
  } finally {
    loader.value = false
    isLoading.value = false
  }
}

const removeConfirm = (item, event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  if (!props.canDelete(item)) return
  choosed.value = item
  dialogDelete.value = true
}

const removeAcept = async () => {
  dialogDelete.value = false
  selected.value = null
  await remove(route.value, choosed.value.id, 'recurso')
  await loadItems(1)
  emits('removedItem')
}

const filterAll = async (event) => {
  filters.value = ensureMandatoryFilters(event && event.params ? event.params : [])
  tableControl.value.page = 1
  saveState()
  await loadItems(2)
}

const handleSelectItem = (item) => {
  selected.value = item
}

const handleEdit = (item) => {
  if (props.editInNewTab) {
    const editParams = props.protocolRedirect ? { protocolo: item.protocolo } : { id: item.id }
    const resolved = router.resolve({
      name: paramRouteEdit.value,
      params: editParams,
    })
    window.open(resolved.href, '_blank')
    return
  }

  if (props.protocolRedirect) {
    return navigate(paramRouteEdit.value, { protocolo: item.protocolo }, true)
  }
  navigate(paramRouteEdit.value, { id: item.id }, true)
}

const handleDoubleClick = (item) => {
  if (props.protocolRedirect) {
    const resolved = router.resolve({
      name: paramRouteEdit.value,
      params: { protocolo: item.protocolo },
    })
    window.open(resolved.href, '_blank')
    return
  }
  handleEdit(item)
}

const handleHeadersChange = (value = []) => {
  const sanitized = (Array.isArray(value) ? value : []).filter((header) => header?.field && header.field !== 'actions')
  if (!sanitized.length) return

  const widths = sanitized.reduce((acc, header) => {
    if (header.width === null || header.width === undefined || header.width === '') return acc
    acc[header.field] = header.width
    return acc
  }, {})

  if (!Array.isArray(allHeaders.value)) {
    headers.value = applyHeaderLayout(
      headers.value,
      sanitized.map((header) => header.field),
      widths,
    )
    headersLocal.value = [...headers.value, buildActionHeader()]
    saveState()
    return
  }

  const fixedFields = new Set(headers.value.filter((header) => header.fixed).map((header) => header.field))
  const fixedOrder = sanitized.filter((header) => fixedFields.has(header.field)).map((header) => header.field)
  const selectableOrder = sanitized.filter((header) => !fixedFields.has(header.field)).map((header) => header.field)

  headers.value = applyHeaderLayout(headers.value, fixedOrder, widths)
  allHeaders.value = applyHeaderLayout(allHeaders.value, selectableOrder, widths)
  headersLocal.value = [...headers.value, buildActionHeader()]
  saveState()
}

const handleResetLayout = () => {
  resetHeaderState()
  saveState()
}

watch(
  () => tableControl.value,
  async () => {
    if (initializing.value) return
    await loadItems(3)
    saveState()
  },
  { deep: true },
)

watch(
  () => props.refresh,
  async (newVal) => {
    if (!newVal) return
    if (initializing.value) return
    await loadItems(4)
    emits('update:refresh', false)
  },
)

watch(
  () => selectHeaders.value,
  () => {
    saveState()
  },
  { deep: true },
)

watch(
  () => props.modelValue.headers,
  (newHeaders) => {
    headers.value = newHeaders ?? []
    headersLocal.value = [...headers.value, buildActionHeader()]
  },
  { deep: true },
)

watch(
  () => props.modelValue.allHeaders,
  (newHeaders) => {
    allHeaders.value = newHeaders
    if (!Array.isArray(newHeaders)) return

    const allowedFields = newHeaders.map((item) => item.field)
    selectHeaders.value = selectHeaders.value.filter((field) => allowedFields.includes(field))

    if (selectHeaders.value.length === 0) {
      selectHeaders.value = allowedFields
    }
  },
  { deep: true },
)

watch(
  () => props.modelValue.order,
  async (newOrder, oldOrder) => {
    if (initializing.value) return
    if (JSON.stringify(newOrder ?? []) === JSON.stringify(oldOrder ?? [])) return

    tableControl.value.order = Array.isArray(newOrder) ? [...newOrder] : []
    tableControl.value.page = 1
  },
  { deep: true },
)

watch(
  () => props.modelValue.orderResetKey,
  (newValue, oldValue) => {
    if (initializing.value) return
    if (newValue === oldValue) return

    tableControl.value.order = Array.isArray(props.modelValue.order) ? [...props.modelValue.order] : []
    tableControl.value.page = 1
  },
)

watch(
  () => props.modelValue.filterPerene,
  async (newValue, oldValue) => {
    filterPerene.value = newValue
    if (initializing.value) return
    if (JSON.stringify(newValue ?? []) === JSON.stringify(oldValue ?? [])) return
    tableControl.value.page = 1
    await loadItems(6)
  },
  { deep: true },
)

watch(exportLoader, (newVal) => {
  loader.value = newVal
})

onBeforeMount(() => {
  loadState()
})

onMounted(async () => {
  await loadItems()
  initializing.value = false
})
</script>
